/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTable from "@insite/mobius/DataTable";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import TextField from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import Notify from "@stoneage/App/Components/Notify";
import filterLookupProducts from "@stoneage/App/Handlers/QuickProductLookup/filterProductsByNameOrPartNumber";
import getProductPartList from "@stoneage/App/Handlers/QuickProductLookup/getProductPartListProducts";
import getQuickProductLookups from "@stoneage/App/Handlers/QuickProductLookup/getQuickProductLookup";
import paginationChangeHandler from "@stoneage/App/Handlers/QuickProductLookup/paginationChangeHandler";
import { BundleElement } from "@stoneage/App/Services/CustomRealTimeInventory/CustomRealTimeInventoryService";
import { ProductPartList } from "@stoneage/App/Services/QuickProductLookup/QuickProductLookupService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { isDealer } from "@stoneage/App/Store/Selectors/GeneralSelectors";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaQuickProductLookupEvent,
} from "@stoneage/App/Utilities/DataLayer";
import Pagination, { PaginationPresentationProps } from "@stoneage/MobiusOverrides/Pagination/Pagination";
import ProductAvailability from "@stoneage/Overrides/Components/ProductAvailability";
import { getCustomDealerSiteMessage } from "@stoneage/XDL/Helpers";
import IconSearch from "@stoneage/XDL/Icons/IconSearch";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    isLoading: state.xProductLookup.isLoading,
    products: state.xProductLookup.paginatedProducts,
    quickProductsUrl: getPageLinkByPageType(state, "QuickProductLookupPage")?.url,
    isMyAccountPage: getCurrentPage(state)?.type === "MyAccountPage",
    pagination: state.xProductLookup.pagination,
    productPartList: state.xProductLookup.productPartList,
    isLoadingProductList: state.xProductLookup.isLoadingPartList,
    isDealer: isDealer(state),
    customerNumber: getBillToCustomerNumber(state),
    quickProductLookupEvent: state.xGoogleAnalytics.quickProductLookupEvent,
    impersonation: trackAsImpersonation(state),
    sessionCurrency: state.context.session.currency,
});

const mapDispatchToProps = {
    getQuickProductLookups,
    filterLookupProducts,
    paginationChangeHandler,
    getProductPartList,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

const Cell = styled.span`
    padding: 8px 12px;
    position: relative;
    overflow: hidden;
    text-overflow: ellipsis;
    @media (max-width: 767px) {
        &&& {
            overflow: visible;
            width: 100%;
            padding: 8px 12px 15px 0;
            position: relative;
            text-align: left;
            &:before {
                content: attr(data-label);
                position: absolute;
                top: -9px;
                font-size: 12px;
                font-weight: bold;
            }
        }
    }
`;

const PartIDCell = styled(Cell)`
    width: 15%;
    @media (max-width: 768px) {
        &.hide-on-small {
            display: none;
        }
    }
`;
const DescriptionCell = styled(Cell)`
    width: 35%;
`;
const InventoryCell = styled(Cell)`
    width: 15%;
`;
const PriceCell = styled(Cell)`
    width: 14%;
    padding: 8px 2px;
    text-align: center;
`;
const DiscountPercentCell = styled(Cell)`
    width: 7%;
    padding: 8px;
    text-align: center;
`;
const DiscountAmountCell = styled(Cell)`
    width: 14%;
    padding: 8px 2px;
    text-align: center;
`;

const PartListCell = styled(Typography)`
    font-size: 12px;
    line-height: 18px;
    text-align: left;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: 768px) {
        &&& {
            width: 50%;
            padding: 5px;
            &:before {
                content: attr(data-label);
                font-weight: bold;
                margin-right: 5px;
            }
        }
    }
`;

export interface QuickProductLookupStyles {
    spinnerContainer?: InjectableCss;
    container?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    viewAllLink?: LinkPresentationProps;
    table?: DataTablePresentationProps;
    showAllLink?: LinkProps;
    pagination?: PaginationPresentationProps;
    dataRow?: InjectableCss;
    partListWrapper?: InjectableCss;
}

const styles: QuickProductLookupStyles = {
    spinnerContainer: {
        css: css`
            width: 100%;
            height: 200px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            margin-bottom: 12px;
        `,
    },
    title: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
            text-transform: capitalize;
        `,
    },
    viewAllLink: {
        color: "secondary",
        css: css`
            > span {
                display: flex;
                align-items: center;
                margin-left: 32px;
                font-size: 15px;
                font-weight: bold;
            }
        `,
    },
    showAllLink: {
        typographyProps: {
            size: 12,
            lineHeight: "18px",
            css: css`
                display: inline-flex;
                align-items: center;
            `,
        },
        color: "secondary",
        css: css`
            display: flex;
            margin-top: 12px;
            margin-bottom: 6px;
            @media (max-width: 767px) {
                margin-top: 0;
            }
        `,
    },
    dataRow: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding-bottom: 8px;
            border-bottom: 1px solid ${getColor("common.ui5")};
            &:nth-child(2n) {
                background: ${getColor("common.ui4")};
            }
            @media (max-width: 767px) {
                padding-top: 15px;
                padding-left: 5px;
            }
        `,
    },
    partListWrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                justify-content: flex-end;
            `,
        },
    },
};

const QuickProductLookup = ({
    isLoading,
    products,
    getQuickProductLookups,
    filterLookupProducts,
    paginationChangeHandler,
    getProductPartList,
    quickProductsUrl,
    isMyAccountPage,
    pagination,
    productPartList,
    isLoadingProductList,
    toaster,
    isDealer,
    quickProductLookupEvent,
    customerNumber,
    impersonation,
    sessionCurrency,
}: Props) => {
    const [filter, setFilter] = useState("");

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value;
        setFilter(searchTerm);
        filterLookupProducts({ filter: searchTerm });
    };
    const debounceResults = useMemo(() => {
        return debounce(handleFilterChange, 300);
    }, []);

    const handleSearchInputBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value;
        if (!searchTerm || !quickProductLookupEvent) {
            return;
        }
        trackGaQuickProductLookupEvent(searchTerm, impersonation, customerNumber);
    };

    useEffect(() => {
        if (!products || !products.length) {
            getQuickProductLookups({
                onError: error => {
                    toaster.addToast({
                        body: error?.message,
                        messageType: "danger",
                    });
                },
            });
        }
    }, []);

    useEffect(() => {
        return () => {
            debounceResults.cancel();
        };
    });

    useEffect(() => {
        filterLookupProducts({ filter: "" });
    }, []);

    if ((!products || !products?.length) && !isLoading && !filter.length) {
        return isMyAccountPage ? null : <Notify messageType="info" body={translate("No products are available")} />;
    }

    if (!products && isLoading) {
        return (
            <StyledWrapper {...styles.spinnerContainer}>
                <LoadingSpinner size={30} />
            </StyledWrapper>
        );
    }

    const changePage = (newPageIndex: number) => {
        paginationChangeHandler({ page: newPageIndex });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        paginationChangeHandler({ pageSize: newPageSize });
    };

    return (
        <StyledWrapper>
            {isMyAccountPage && (
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography as="h3" {...styles.title}>
                        {translate("Quick Product Lookup")}
                    </Typography>
                    <Link href={quickProductsUrl} {...styles.viewAllLink}>
                        {translate("View All")}
                        <Icon
                            src="ChevronRight"
                            size={14}
                            color="secondary"
                            css={css`
                                margin-left: 5px;
                            `}
                        />
                    </Link>
                </StyledWrapper>
            )}
            <TextField
                onChange={debounceResults}
                onBlur={handleSearchInputBlur}
                placeholder={translate("Search by Product Name, Product Code, Part # or Keywords")}
                cssOverrides={{
                    formField: css`
                        margin-bottom: 16px;
                    `,
                    inputSelect: css`
                        padding-right: 34px;
                        text-overflow: ellipsis;
                    `,
                }}
                iconClickableProps={{
                    onClick: () => filterLookupProducts({ filter }),
                    css: css`
                        &&& {
                            padding: 4px;
                        }
                    `,
                }}
                iconProps={{
                    src: IconSearch,
                    size: 19,
                    color: "primary",
                    css: css`
                        &&& {
                            padding: 4px;
                        }
                    `,
                }}
            />
            <StyledWrapper>
                <DataTable
                    css={css`
                        @media (max-width: 767px) {
                            display: none;
                        }
                    `}
                >
                    <DataTableHead>
                        <DataTableHeader
                            css={css`
                                width: 15%;
                            `}
                            mergeCss={true}
                            title={translate("Part ID")}
                        >
                            {translate("Part ID")}
                        </DataTableHeader>
                        <DataTableHeader
                            css={css`
                                width: 35%;
                            `}
                            mergeCss={true}
                            title={translate("Description")}
                        >
                            {translate("Description")}
                        </DataTableHeader>
                        <DataTableHeader
                            css={css`
                                width: 15%;
                            `}
                            mergeCss={true}
                            title={translate("Inventory")}
                        >
                            {translate("Inventory")}
                        </DataTableHeader>
                        <DataTableHeader
                            css={css`
                                width: 14%;
                                padding: 8px 2px;
                                text-align: center;
                            `}
                            mergeCss={true}
                            title={translate("MSRP")}
                        >
                            {translate("MSRP")}
                        </DataTableHeader>
                        <DataTableHeader
                            css={css`
                                width: 7%;
                                padding: 8px;
                                text-align: center;
                            `}
                            mergeCss={true}
                            title={translate("Disc. %")}
                        >
                            {translate("Disc. %")}
                        </DataTableHeader>
                        <DataTableHeader
                            css={css`
                                width: 14%;
                                padding: 8px 2px;
                                text-align: center;
                            `}
                            mergeCss={true}
                            title={translate("Cost")}
                        >
                            {translate("Cost")}
                        </DataTableHeader>
                    </DataTableHead>
                </DataTable>
                <StyledWrapper
                    css={css`
                        @media (max-width: 767px) {
                            border-top: 10px solid #666666;
                        }
                    `}
                >
                    {products &&
                        products.map(product => {
                            const components: BundleElement[] = [];
                            if (product.billOfMaterials?.length) {
                                product.billOfMaterials.forEach(billOfMaterial => {
                                    const component = {
                                        erpNumber: billOfMaterial.partNumber,
                                        qtyPer: billOfMaterial.qtyPer,
                                    };
                                    components.push(component);
                                });
                            }
                            return (
                                <StyledWrapper
                                    key={`quickProductLookup_${product.productNumber}_${product.productDescription}`}
                                    {...styles.dataRow}
                                >
                                    <PartIDCell data-label={translate("Part ID")} translate="no">
                                        {product.url && product.url.length !== 0 ? (
                                            <Link href={product.url}>{product.productNumber}</Link>
                                        ) : (
                                            product.productNumber
                                        )}
                                    </PartIDCell>
                                    <DescriptionCell data-label={translate("Description")}>
                                        <StyledWrapper>{product.productDescription}</StyledWrapper>
                                    </DescriptionCell>
                                    <InventoryCell data-label={translate("Inventory")}>
                                        <ProductAvailability
                                            productId={product.productId || ""}
                                            trackInventory={!!product.trackInventory}
                                            unitOfMeasure={product.unitOfMeasure || ""}
                                            isProductDetailsPage={false}
                                            availability={{
                                                message: isDealer
                                                    ? (siteMessage(
                                                          getCustomDealerSiteMessage(
                                                              product.availability.toLowerCase() ===
                                                                  "dealerpricingavailable"
                                                                  ? 1
                                                                  : 3,
                                                          ),
                                                          `${product.availableQty || 0}`,
                                                      ) as string)
                                                    : (siteMessage(`${product.availability}`) as string),
                                                messageType:
                                                    product.availability.toLowerCase() === "dealerpricingavailable"
                                                        ? 1
                                                        : 3,
                                                requiresRealTimeInventory: true,
                                            }}
                                            {...({
                                                isSingleProduct: !product.hasComponents,
                                                isInCatalog: (product.url && product.url?.length > 0) || false,
                                                productNumber: product.productNumber,
                                                components,
                                                customInventoryCall: true,
                                            } as any)}
                                        />
                                    </InventoryCell>
                                    <PriceCell data-label={translate("MSRP")} translate="no">
                                        {product.manufacturerSalePriceDisplay || "--.--"}
                                    </PriceCell>
                                    <DiscountPercentCell data-label={translate("Disc. %")}>
                                        {product.discountPercentDisplay}
                                    </DiscountPercentCell>
                                    <DiscountAmountCell data-label={translate("Cost")} translate="no">
                                        {product.discountPriceDisplay ||
                                            `${sessionCurrency?.currencySymbol || sessionCurrency?.currencyCode}--.--`}
                                    </DiscountAmountCell>
                                    <StyledWrapper {...styles.partListWrapper}>
                                        <PartIDCell className="hide-on-small"></PartIDCell>
                                        {product.hasComponents && (
                                            <StyledWrapper
                                                css={css`
                                                    padding-left: 12px;
                                                    @media (max-width: 767px) {
                                                        padding-left: 0;
                                                        width: 100%;
                                                    }
                                                `}
                                            >
                                                <ProductLookupParts
                                                    productNumber={product.productNumber}
                                                    getProductPartList={getProductPartList}
                                                    productPartList={productPartList}
                                                    isLoadingProductList={isLoadingProductList}
                                                    isDealer={isDealer}
                                                />
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                </StyledWrapper>
            </StyledWrapper>
            {!isMyAccountPage && (
                <Pagination
                    {...styles.pagination}
                    resultsCount={pagination.totalItemCount}
                    currentPage={pagination.page}
                    resultsPerPage={pagination.pageSize}
                    resultsPerPageOptions={pagination.pageSizeOptions}
                    onChangePage={changePage}
                    onChangeResultsPerPage={changeResultsPerPage}
                    data-test-selector="productLookip_pagination"
                />
            )}
        </StyledWrapper>
    );
};

type PartProductsProps = {
    productNumber: string;
    getProductPartList: ({ productNumber }: { productNumber: string }) => void;
    productPartList: { [key: string]: ProductPartList[] };
    isLoadingProductList: { [key: string]: boolean };
    isDealer: boolean;
};

const ProductLookupParts = ({
    productNumber,
    getProductPartList,
    productPartList,
    isLoadingProductList,
    isDealer,
}: PartProductsProps) => {
    const showPartList = () => {
        if (!productPartList?.[productNumber]?.length) {
            getProductPartList({
                productNumber,
            });
        }
        setShowPartProducts(!showPartProducts);
    };

    const [showPartProducts, setShowPartProducts] = useState(false);
    return (
        <>
            <Link {...styles.showAllLink} onClick={showPartList}>
                {translate("Show All Included Parts")}
                {!showPartProducts && (
                    <Icon
                        size={14}
                        src="ChevronDown"
                        css={css`
                            &&& {
                                margin-left: 6px;
                            }
                        `}
                    />
                )}
                {showPartProducts && (
                    <Icon
                        size={14}
                        src="ChevronUp"
                        css={css`
                            &&& {
                                margin-left: 6px;
                            }
                        `}
                    />
                )}
            </Link>
            {showPartProducts && (
                <>
                    {isLoadingProductList?.[productNumber] && (
                        <StyledWrapper
                            css={css`
                                display: flex;
                                align-items: center;
                                justify-content: center;
                            `}
                        >
                            <LoadingSpinner size={15} />
                        </StyledWrapper>
                    )}
                    {!isLoadingProductList?.[productNumber] && (
                        <>
                            <StyledWrapper
                                css={css`
                                    @media (max-width: 768px) {
                                        display: none;
                                    }
                                `}
                            >
                                <PartListCell
                                    weight="bold"
                                    css={css`
                                        width: 40px;
                                        @media (max-width: 767px) {
                                            width: 30%;
                                        }
                                    `}
                                >
                                    {translate("Qty")}
                                </PartListCell>
                                <PartListCell
                                    weight="bold"
                                    css={css`
                                        width: 160px;
                                        @media (max-width: 767px) {
                                            width: 30%;
                                        }
                                    `}
                                >
                                    {translate("Part#")}
                                </PartListCell>
                                <PartListCell
                                    weight="bold"
                                    css={css`
                                        width: 175px;
                                        @media (max-width: 767px) {
                                            width: 30%;
                                        }
                                    `}
                                >
                                    {translate("Name")}
                                </PartListCell>
                                <PartListCell
                                    weight="bold"
                                    css={css`
                                        width: 100px;
                                    `}
                                >
                                    {translate("Inventory")}
                                </PartListCell>
                                <PartListCell
                                    weight="bold"
                                    css={css`
                                        width: 100px;
                                        @media (max-width: 767px) {
                                            width: 20%;
                                        }
                                    `}
                                >
                                    {translate("MSRP")}
                                </PartListCell>
                                <PartListCell
                                    weight="bold"
                                    css={css`
                                        width: 100px;
                                        @media (max-width: 767px) {
                                            width: 20%;
                                        }
                                    `}
                                >
                                    {translate("Disc. %")}
                                </PartListCell>
                                <PartListCell
                                    weight="bold"
                                    css={css`
                                        width: 100px;
                                        @media (max-width: 767px) {
                                            width: 20%;
                                        }
                                    `}
                                >
                                    {translate("Cost")}
                                </PartListCell>
                            </StyledWrapper>
                            {productPartList?.[productNumber]?.map(product => (
                                <StyledWrapper
                                    key={uniqueId()}
                                    className="parts-row"
                                    css={css`
                                        @media (max-width: 768px) {
                                            display: flex;
                                            justify-content: space-between;
                                            flex-wrap: wrap;
                                            padding: 5px;
                                            border-bottom: 1px solid #cccccc;
                                            &:last-child {
                                                border-bottom: 0;
                                            }
                                        }
                                    `}
                                >
                                    <PartListCell
                                        css={css`
                                            width: 40px;
                                        `}
                                        translate="no"
                                        data-label={translate("Qty:")}
                                    >
                                        {product.qtyPer}
                                    </PartListCell>
                                    <PartListCell
                                        css={css`
                                            width: 160px;
                                            padding-right: 15px;
                                        `}
                                        translate="no"
                                        data-label={translate("Part#:")}
                                    >
                                        {product.url && product.url.length !== 0 ? (
                                            <Link href={product.url}>{product.productNumber}</Link>
                                        ) : (
                                            product.productNumber
                                        )}
                                    </PartListCell>
                                    <PartListCell
                                        css={css`
                                            width: 175px;
                                            padding-right: 15px;
                                        `}
                                        data-label={translate("Name:")}
                                    >
                                        {product.productDescription}
                                    </PartListCell>
                                    <PartListCell
                                        css={css`
                                            width: 100px;

                                            ${IconWrapper} {
                                                display: none;
                                            }
                                        `}
                                        data-label={translate("Inventory:")}
                                    >
                                        <ProductAvailability
                                            productId={product.productId || ""}
                                            trackInventory={!!product.trackInventory}
                                            unitOfMeasure={product.unitOfMeasure || ""}
                                            isProductDetailsPage={false}
                                            availability={{
                                                message: isDealer
                                                    ? (siteMessage(
                                                          getCustomDealerSiteMessage(
                                                              product.availability.toLowerCase() ===
                                                                  "dealerpricingavailable"
                                                                  ? 1
                                                                  : 3,
                                                          ),
                                                          `${product.availableQty || 0}`,
                                                      ) as string)
                                                    : (siteMessage(`${product.availability}`) as string),
                                                messageType:
                                                    product.availability.toLowerCase() === "dealerpricingavailable"
                                                        ? 1
                                                        : 3,
                                                requiresRealTimeInventory: true,
                                            }}
                                            {...({
                                                isSingleProduct: true,
                                                isInCatalog: (product.url && product.url?.length > 0) || false,
                                                productNumber: product.productNumber,
                                                components: [],
                                                customInventoryCall: true,
                                            } as any)}
                                        />
                                    </PartListCell>
                                    <PartListCell
                                        css={css`
                                            width: 100px;
                                        `}
                                        translate="no"
                                        data-label={translate("MSRP:")}
                                    >
                                        {product.manufacturerSalePriceDisplay}
                                    </PartListCell>
                                    <PartListCell
                                        css={css`
                                            width: 100px;
                                        `}
                                        translate="no"
                                        data-label={translate("Desc. %:")}
                                    >
                                        {product.discountPercentDisplay || "-%"}
                                    </PartListCell>
                                    <PartListCell
                                        css={css`
                                            width: 100px;
                                        `}
                                        translate="no"
                                        data-label={translate("Cost:")}
                                    >
                                        {product.discountPriceDisplay}
                                    </PartListCell>
                                </StyledWrapper>
                            ))}
                        </>
                    )}
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(QuickProductLookup)),
    definition: {
        group: "Quick Product Lookup" as any,
        displayName: "Quick Product Lookup",
    },
};

export default widgetModule;
